import { useEffect, useState } from "react";

import type { LeaderboardApprover } from "./DraftLeaderboardPanel";
import {
  leaderboardApproverOptionLabel,
  mapAdminUserToLeaderboardApprover,
} from "./leaderboardApproverUtils";
import Button from "../../Components/Button";
import Modal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import { fetchAdminUsers } from "../../Store/Reducers/MasterSlice";
import { changeApprover } from "../../Store/Reducers/SponsorEngagementSlice";
import { toast } from "../../Utils/Helper/toast";

type ChangeApproverModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  initialApproverId?: string | null;
};

const ChangeApproverModal = ({
  isOpen,
  onClose,
  onSuccess,
  initialApproverId = "",
}: ChangeApproverModalProps) => {
  const dispatch = useAppDispatch();
  const [approverId, setApproverId] = useState("");
  const [approvers, setApprovers] = useState<LeaderboardApprover[]>([]);
  const [approversLoading, setApproversLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const isUnchangedSelection =
    Boolean(initialApproverId) && approverId === initialApproverId;

  const currentApproverLabel = approvers.find(
    a => a.user_id === initialApproverId
  );

  useEffect(() => {
    if (!isOpen) return;
    setApproverId("");
    setApproversLoading(true);
    dispatch(fetchAdminUsers())
      .then(result => {
        if (fetchAdminUsers.fulfilled.match(result)) {
          const users = Array.isArray(result.payload) ? result.payload : [];
          setApprovers(users.map(mapAdminUserToLeaderboardApprover));
        } else {
          toast.error((result.payload as string) || "Failed to load approvers");
          setApprovers([]);
        }
      })
      .finally(() => setApproversLoading(false));
  }, [dispatch, isOpen, initialApproverId]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!approverId) {
      toast.error("Select an approver");
      return;
    }
    if (isUnchangedSelection) {
      toast.info("This approver is already assigned");
      onClose();
      return;
    }
    setSubmitting(true);
    try {
      const result = await dispatch(
        changeApprover({ approver_id: approverId })
      );
      if (changeApprover.rejected.match(result)) {
        toast.error((result.payload as string) || "Failed to change approver");
        return;
      }
      toast.success("Approver updated");
      await onSuccess();
      onClose();
    } catch (e: unknown) {
      toast.error(
        (e as any)?.response?.data?.detail ||
          (e as any)?.message ||
          "Failed to change approver"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      title="Change Approver"
      subtitle="Select a different approver for this pending submission."
      size="lg"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            loading={submitting}
            disabled={!approverId || approversLoading || isUnchangedSelection}
          >
            Update Approver
          </Button>
        </>
      }
    >
      {initialApproverId && currentApproverLabel ? (
        <p className="text-sm text-gray-600 mb-4">
          Current approver:{" "}
          <strong>
            {leaderboardApproverOptionLabel(currentApproverLabel)}
          </strong>
        </p>
      ) : null}
      <label className="block text-sm font-medium text-gray-700 mb-2">
        New approver <span className="text-red-500">*</span>
      </label>
      <select
        className="w-full border border-gray-300 rounded-lg p-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-purple-500/30 focus:border-purple-500"
        value={approverId}
        onChange={e => setApproverId(e.target.value)}
        disabled={approversLoading || submitting}
      >
        <option value="">
          {approversLoading ? "Loading approvers…" : "Select approver"}
        </option>
        {approvers.map(a => (
          <option key={a.user_id} value={a.user_id}>
            {leaderboardApproverOptionLabel(a)}
          </option>
        ))}
      </select>
      {isUnchangedSelection ? (
        <p className="text-sm text-gray-600 mt-3 mb-0">
          Select a different approver to reassign this submission.
        </p>
      ) : null}
      {!approversLoading && approvers.length === 0 ? (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4 mb-0">
          No approvers available.
        </p>
      ) : null}
    </Modal>
  );
};

export default ChangeApproverModal;
